/**
 * FP3-028 / FP3-028A / FP3-048 — Public Request Access page.
 *
 * Submits a request via POST /api/access-requests. Does NOT create
 * a user account, never logs the requester in. Confirmation copy
 * makes the invite-only model explicit.
 *
 * FP3-048 changes:
 *   - Adds an explicit "Request type" radio field driving the rest
 *     of the form: school_team | individual_provider | other.
 *   - Organization / School field becomes required for school_team
 *     and optional otherwise, with helper copy that changes per
 *     selection.
 *   - Success message clarifies that no account is created and
 *     we'll follow up by email.
 *
 * Visual: matches the Login page — warm cream background, deep
 * green primary, calm card, no marketing-page energy.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  CheckCircle,
  Buildings,
  Stethoscope,
  Question,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/lib/api";

// FP3-048 — Explicit requester self-classification. Drives the
// required-vs-optional behavior of the organization field on this
// page AND the Super Admin approval modal.
const ACCOUNT_TYPES = [
  {
    v: "school_team",
    title: "School / district / organization team",
    body: "Multiple providers, school admins, or district roles.",
    icon: Buildings,
  },
  {
    v: "individual_provider",
    title: "Individual / solo provider",
    body: "Working on your own caseload — we'll create a solo workspace.",
    icon: Stethoscope,
  },
  {
    v: "other",
    title: "Other / not sure",
    body: "Something else — we'll follow up to learn more.",
    icon: Question,
  },
];

const ROLES = [
  { v: "individual_provider", l: "Individual provider" },
  { v: "school_team", l: "School team" },
  { v: "administrator", l: "Administrator" },
  { v: "other", l: "Other" },
];

const SPECIALTIES = [
  "", "SLP", "SLPA", "Special Education Teacher", "OT", "COTA",
  "PT", "PTA", "School Psychologist", "Counselor", "BCBA",
  "Behavior Specialist", "Interventionist", "Reading Specialist", "Other",
];

export default function RequestAccess() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    requester_first_name: "",
    requester_last_name: "",
    email: "",
    phone: "",
    organization_or_school_name: "",
    role_interest: "individual_provider",
    provider_specialty_interest: "",
    expected_use: "individual",
    message: "",
    // FP3-048 — Source of truth for the org-field requirement.
    account_type: "individual_provider",
    // FP3-052 — Solo provider assistant add-on request. Captured here
    // as metadata only; no billing, no automatic assistant invite.
    wants_assistant_addons: false,
    requested_assistant_count: 1,
    requested_assistant_types: [],
    assistant_request_note: "",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const orgRequired = form.account_type === "school_team";
  const orgLabel = orgRequired ? "Organization / School" : "Organization · optional";
  const orgHelper =
    form.account_type === "school_team"
      ? "Required for school or team access."
      : form.account_type === "individual_provider"
        ? "Optional for solo providers. If left blank, we'll create a solo workspace for you."
        : "Optional — we'll work with you on the right organization setup during review.";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (orgRequired && !form.organization_or_school_name.trim()) {
      toast.error("Organization / School name is required for school or team access.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createAccessRequest(form);
      setSubmitted(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not submit request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="request-access-confirmation-page"
        className="min-h-screen bg-[#F7F5EF] body-font flex items-center justify-center px-5 py-10"
      >
        <Card
          className="w-full max-w-md border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent
            data-testid="access-request-confirmation"
            className="p-8 text-center"
          >
            <Brand small className="justify-center mb-6" />
            <div className="w-12 h-12 mx-auto rounded-full bg-[#E8F0EB] flex items-center justify-center mb-4">
              <CheckCircle size={26} weight="duotone" className="text-[#1E3F33]" />
            </div>
            <div
              data-testid="access-request-confirmation-pill"
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
            >
              Request received
            </div>
            <h1 className="text-[22px] font-semibold text-[#1F2937] heading-font mb-3 leading-tight">
              Request received. We&rsquo;ll follow up by email.
            </h1>
            <p className="text-[13.5px] text-[#4B5563] leading-relaxed mb-2">
              Frame Practice access is currently invite-only. We&rsquo;ll review
              your request and follow up by email if it&rsquo;s a fit.
            </p>
            <p className="text-[12px] text-[#6B7280] leading-relaxed mb-6">
              Submitting this form does <strong>not</strong> create an
              account.
            </p>
            <Link
              to="/login"
              data-testid="confirmation-back-to-login"
              className="inline-block text-[12.5px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
            >
              ← Back to sign in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      data-testid="request-access-page"
      className="min-h-screen bg-[#F7F5EF] body-font"
    >
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-10 lg:py-14">
        <div className="mb-7">
          <Brand />
        </div>

        <Card
          data-testid="request-access-card"
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-7 sm:p-9">
            <div className="mb-6">
              <div
                data-testid="request-access-invite-only-pill"
                className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
              >
                <ShieldCheck size={11} weight="duotone" />
                Invite-only access
              </div>
              <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
                Request access
              </h1>
              <p className="text-[13px] text-[#4B5563] mt-2 leading-relaxed max-w-lg">
                Tell us a bit about you and we&rsquo;ll be in touch if it&rsquo;s a fit.
                Submitting this form does <strong>not</strong> create an
                account — access is granted by invitation after review.
              </p>

              {/* FP3-069 — Solo provider Direct Pilot CTA. Visible
                  whenever the requester self-classifies as a solo
                  provider. Routes them to the new /start-pilot flow
                  which creates the workspace immediately, no
                  SuperAdmin approval required. */}
              {form.account_type === "individual_provider" && (
                <div
                  data-testid="solo-direct-pilot-cta"
                  className="mt-4 rounded-xl border border-[#1E3F33]/30 bg-[#E8F0EB] p-4 flex items-start justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] font-semibold mb-1">
                      Solo provider · faster path
                    </div>
                    <div className="text-[13px] font-semibold text-[#1F2937] heading-font leading-tight">
                      Skip the wait — start your Pilot workspace now.
                    </div>
                    <p className="text-[12px] text-[#4B5563] mt-1 leading-relaxed">
                      Solo providers can create a workspace
                      immediately. No review, no billing, no
                      credit card.
                    </p>
                  </div>
                  <Link
                    to="/start-pilot"
                    data-testid="solo-direct-pilot-cta-link"
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-[#1E3F33] hover:bg-[#173127] rounded-lg px-3.5 py-2 transition-colors"
                  >
                    Start Pilot →
                  </Link>
                </div>
              )}
            </div>

            <form
              data-testid="access-request-form"
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4"
            >
              {/* FP3-048 — Account type / request type. Spans both
                  columns so the radio cards have room to breathe.
                  Each card is keyboard- and screen-reader-friendly
                  via the <label> wrapping the <input type="radio">. */}
              <div className="sm:col-span-2">
                <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
                  Request type<span className="text-[#9C6B2E] ml-0.5">*</span>
                </Label>
                <div
                  data-testid="req-account-type-group"
                  role="radiogroup"
                  className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2.5"
                >
                  {ACCOUNT_TYPES.map((opt) => {
                    const Icon = opt.icon;
                    const active = form.account_type === opt.v;
                    return (
                      <label
                        key={opt.v}
                        data-testid={`req-account-type-${opt.v}`}
                        className={`cursor-pointer rounded-xl border p-3.5 transition-colors ${
                          active
                            ? "border-[#1E3F33] bg-[#E8F0EB]"
                            : "border-[#E5E3DB] bg-white hover:border-[#8FA998]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="account_type"
                          value={opt.v}
                          checked={active}
                          onChange={() => upd("account_type", opt.v)}
                          className="sr-only"
                        />
                        <div className="flex items-start gap-2.5">
                          <div
                            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                              active ? "bg-[#1E3F33]" : "bg-[#E8F0EB]"
                            }`}
                          >
                            <Icon
                              size={16}
                              weight="duotone"
                              className={active ? "text-white" : "text-[#1E3F33]"}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold text-[#1F2937] heading-font leading-tight">
                              {opt.title}
                            </div>
                            <div className="text-[11px] text-[#6B7280] mt-1 leading-snug">
                              {opt.body}
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <FieldShell label="First name" required>
                <Input
                  data-testid="req-first-name"
                  value={form.requester_first_name}
                  onChange={(e) => upd("requester_first_name", e.target.value)}
                  required
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell label="Last name" required>
                <Input
                  data-testid="req-last-name"
                  value={form.requester_last_name}
                  onChange={(e) => upd("requester_last_name", e.target.value)}
                  required
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell label="Email" required colSpan2>
                <Input
                  data-testid="req-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => upd("email", e.target.value)}
                  required
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                  placeholder="you@school.org"
                />
              </FieldShell>
              <FieldShell label="Phone · optional">
                <Input
                  data-testid="req-phone"
                  value={form.phone}
                  onChange={(e) => upd("phone", e.target.value)}
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell
                label={orgLabel}
                required={orgRequired}
                helper={orgHelper}
                helperTestId="req-org-helper"
              >
                <Input
                  data-testid="req-org"
                  value={form.organization_or_school_name}
                  onChange={(e) =>
                    upd("organization_or_school_name", e.target.value)
                  }
                  required={orgRequired}
                  placeholder={
                    form.account_type === "individual_provider"
                      ? "Leave blank for a solo workspace"
                      : ""
                  }
                  className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                />
              </FieldShell>
              <FieldShell label="Role">
                <select
                  data-testid="req-role"
                  value={form.role_interest}
                  onChange={(e) => upd("role_interest", e.target.value)}
                  className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33] transition-colors"
                >
                  {ROLES.map((r) => (
                    <option key={r.v} value={r.v}>
                      {r.l}
                    </option>
                  ))}
                </select>
              </FieldShell>
              <FieldShell label="Provider specialty · optional">
                <select
                  data-testid="req-specialty"
                  value={form.provider_specialty_interest}
                  onChange={(e) =>
                    upd("provider_specialty_interest", e.target.value)
                  }
                  className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33] transition-colors"
                >
                  {SPECIALTIES.map((s) => (
                    <option key={s || "none"} value={s}>
                      {s || "—"}
                    </option>
                  ))}
                </select>
              </FieldShell>
              <FieldShell label="Message · optional" colSpan2>
                <Textarea
                  data-testid="req-message"
                  value={form.message}
                  onChange={(e) => upd("message", e.target.value)}
                  placeholder="Anything we should know? (caseload size, school, timing, etc.)"
                  className="min-h-[96px] border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] resize-y"
                />
              </FieldShell>

              {/* FP3-052 — Assistant add-on question (solo provider only).
                  Lets the platform owner plan capacity / supervision
                  before approving. Metadata only; no billing today. */}
              {form.account_type === "individual_provider" && (
                <div
                  data-testid="req-assistants-section"
                  className="sm:col-span-2 rounded-md border border-[#E5E3DB] bg-[#F9F8F6] p-4 mt-1 space-y-3"
                >
                  <div>
                    <p className="text-[12.5px] font-semibold text-[#1F2937]">
                      Will you need assistant provider access?
                    </p>
                    <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
                      Assistant access is for supervised providers such as
                      SLPAs, COTAs, or PTAs working under the primary
                      provider&rsquo;s workspace. Final access is reviewed before
                      invites are sent.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    {[
                      { v: false, l: "No, just me" },
                      { v: true, l: "Yes, I may need one or more assistants" },
                    ].map((opt) => {
                      const active = form.wants_assistant_addons === opt.v;
                      return (
                        <label
                          key={String(opt.v)}
                          data-testid={`req-wants-assistants-${opt.v ? "yes" : "no"}`}
                          className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-[12.5px] transition-colors ${
                            active
                              ? "border-[#1E3F33] bg-white ring-1 ring-[#1E3F33]/20 text-[#1E3F33]"
                              : "border-[#E5E3DB] bg-white text-[#4B5563] hover:border-[#9AA0A6]"
                          }`}
                        >
                          <input
                            type="radio"
                            name="wants_assistant_addons"
                            checked={active}
                            onChange={() =>
                              upd("wants_assistant_addons", opt.v)
                            }
                            className="sr-only"
                          />
                          {opt.l}
                        </label>
                      );
                    })}
                  </div>

                  {form.wants_assistant_addons && (
                    <div
                      data-testid="req-assistants-details"
                      className="space-y-3 pt-1"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FieldShell label="How many assistants?">
                          <Input
                            type="number"
                            min={1}
                            max={20}
                            data-testid="req-assistant-count"
                            value={form.requested_assistant_count}
                            onChange={(e) =>
                              upd(
                                "requested_assistant_count",
                                Math.max(
                                  1,
                                  Math.min(20, parseInt(e.target.value, 10) || 1)
                                )
                              )
                            }
                            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                          />
                        </FieldShell>
                        <FieldShell label="Assistant type(s)">
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {["SLPA", "COTA", "PTA", "Other"].map((t) => {
                              const active =
                                form.requested_assistant_types.includes(t);
                              return (
                                <button
                                  type="button"
                                  key={t}
                                  data-testid={`req-assistant-type-${t.toLowerCase()}`}
                                  onClick={() => {
                                    const set = new Set(
                                      form.requested_assistant_types
                                    );
                                    if (set.has(t)) set.delete(t);
                                    else set.add(t);
                                    upd(
                                      "requested_assistant_types",
                                      Array.from(set)
                                    );
                                  }}
                                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                    active
                                      ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                                      : "bg-white text-[#4B5563] border-[#E5E3DB] hover:border-[#9AA0A6]"
                                  }`}
                                >
                                  {t}
                                </button>
                              );
                            })}
                          </div>
                        </FieldShell>
                      </div>
                      <FieldShell label="Anything else about your assistants? · optional">
                        <Textarea
                          data-testid="req-assistant-note"
                          value={form.assistant_request_note}
                          onChange={(e) =>
                            upd("assistant_request_note", e.target.value)
                          }
                          placeholder="Names / emails if known, supervision arrangement, timing, etc."
                          className="min-h-[64px] border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] resize-y"
                        />
                      </FieldShell>
                    </div>
                  )}
                </div>
              )}

              <div className="sm:col-span-2 flex items-center justify-between mt-3 pt-3 border-t border-[#F1F2F4]">
                <Link
                  to="/login"
                  data-testid="req-back-to-login"
                  className="text-[12.5px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                >
                  ← Back to sign in
                </Link>
                <Button
                  type="submit"
                  data-testid="req-submit"
                  disabled={submitting}
                  className="h-10 px-5 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
                >
                  {submitting ? "Sending…" : "Submit request"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-[#9AA0A6] mt-5">
          © Frame Practice · invite-only provider workspace
        </p>
      </div>
    </div>
  );
}

function Brand({ small = false, className = "" }) {
  const size = small ? "w-9 h-9" : "w-11 h-11";
  const textSize = small ? "text-[16px]" : "text-[19px]";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div
        className={`${size} rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm`}
      >
        <span className="text-white text-base font-bold heading-font">F</span>
      </div>
      <div className="leading-tight">
        <div
          className={`${textSize} font-bold text-[#1E3F33] heading-font tracking-tight`}
        >
          Frame
          <span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
          Provider workspace
        </div>
      </div>
    </div>
  );
}

function FieldShell({ label, required, colSpan2, helper, helperTestId, children }) {
  return (
    <div className={colSpan2 ? "sm:col-span-2" : ""}>
      <Label className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        {label}
        {required && <span className="text-[#9C6B2E] ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {helper && (
        <p
          data-testid={helperTestId}
          className="mt-1.5 text-[11px] text-[#6B7280] leading-snug"
        >
          {helper}
        </p>
      )}
    </div>
  );
}
